import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent,
  Button,
  Stack,
  Divider
} from '@mui/material';
import { 
  LocalShipping as LocalIcon,
  Business as BusinessIcon,
  Storage as StorageIcon,
  Build as SpecialtyIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    icon: <LocalIcon />,
    title: 'Local Moving',
    description: 'Professional local moves within your city',
    features: [
      'Same-day service available',
      'Hourly or flat-rate pricing',
      'Fully equipped trucks',
      'Professional packing'
    ],
    startingPrice: '$99',
    action: 'quote'
  },
  {
    icon: <BusinessIcon />,
    title: 'Long Distance',
    description: 'Cross-country and interprovincial moves',
    features: [
      'Door-to-door service',
      'Real-time tracking',
      'Climate-controlled trucks',
      'Dedicated moving coordinator'
    ],
    startingPrice: '$1,299',
    action: 'quote'
  },
  {
    icon: <StorageIcon />,
    title: 'Storage Solutions',
    description: 'Secure storage for short and long-term needs',
    features: [
      'Climate-controlled units',
      '24/7 security monitoring',
      'Flexible rental terms',
      'Digital inventory management'
    ],
    startingPrice: '$79/month',
    action: 'storage-marketplace'
  },
  {
    icon: <SpecialtyIcon />,
    title: 'Specialty Items',
    description: 'Expert handling of valuable and fragile items',
    features: [
      'Piano and fine art moving',
      'Antique furniture care',
      'Custom crating',
      'White glove service'
    ],
    startingPrice: '$199',
    action: 'quote'
  }
];

const ServicesSection = () => {
  const navigate = useNavigate();

  const handleServiceClick = (action) => {
    if (action === 'storage-marketplace') {
      navigate('/storage-marketplace');
    } else {
      navigate('/quote');
    }
  };
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Typography
            variant="h2"
            fontWeight={700}
            mb={3}
            sx={{ fontSize: { xs: '2rem', md: '3rem' } }}
          >
            Our Services
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
          >
            From local moves to specialty handling, we've got all your moving needs covered
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 4 }}>
          {services.map((service, index) => (
            <Box key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ p: 0, height: '100%' }}>
                  <Stack spacing={3} height="100%">
                    <Box textAlign="center">
                      <Box
                        sx={{
                          color: 'primary.main',
                          mb: 2,
                          fontSize: 48,
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        {service.icon}
                      </Box>
                      
                      <Typography variant="h5" fontWeight={700} mb={1}>
                        {service.title}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {service.description}
                      </Typography>
                    </Box>

                    <Divider />

                    <Stack spacing={1} sx={{ flexGrow: 1 }}>
                      {service.features.map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckIcon sx={{ color: 'success.main', fontSize: 16 }} />
                          <Typography variant="body2">
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>

                    <Box>
                      <Typography 
                        variant="h6" 
                        color="primary.main" 
                        fontWeight={700}
                        textAlign="center"
                        mb={2}
                      >
                        Starting at {service.startingPrice}
                      </Typography>
                      
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => handleServiceClick(service.action)}
                        sx={{
                          py: 1.5,
                          fontWeight: 600,
                          borderRadius: 2
                        }}
                      >
                        {service.action === 'storage-marketplace' ? 'Browse Storage' : 'Get Quote'}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        <Box textAlign="center" mt={8}>
          <Button
            variant="contained"
            size="large"
            sx={{
              px: 6,
              py: 2,
              fontWeight: 600,
              borderRadius: 2,
              fontSize: '1.1rem'
            }}
          >
            View All Services
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;
